import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useProductAlert } from '@/contexts/ProductAlertContext';
import { useProductModal } from '@/contexts/ProductModalContext';
import { toast } from '@/hooks/use-toast';

export const GlobalProductAlert: React.FC = () => {
  const { showProductAlert, productId, closeProductAlert } = useProductAlert();
  const { setIsNewProduct } = useProductModal();
  const navigate = useNavigate();

  const handleCopyLink = () => {
    if (productId) {
      let paymentUrl = '';
      if (import.meta.env.VITE_ENV === 'production') {
        paymentUrl = `${import.meta.env.VITE_PAYMENT_BASE_URL}/${productId}`;
      } else {
        paymentUrl = `${import.meta.env.VITE_BASE_URL}/${productId}`;
      }
      navigator.clipboard.writeText(paymentUrl);
      toast({
        title: "Link copied!",
        description: "Payment link copied to clipboard",
        variant: "default",
      });
    }
    closeProductAlert();
  };

  const handleCreateBundle = () => {
    closeProductAlert();
    
    sessionStorage.setItem('openBundleModal', 'true');

    navigate('/dashboard?tab=bundles');
  };
  
  const handleCreateAnotherProduct = () => {
    closeProductAlert();
    setIsNewProduct(true);
    sessionStorage.setItem('openProductModal', 'true');
    
    navigate('/dashboard?tab=products');
  };

  return (
    <AlertDialog open={showProductAlert} onOpenChange={closeProductAlert}>
      <AlertDialogContent className="bg-white max-w-[600px]">
        <button
          onClick={closeProductAlert}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 focus:outline-none"
          aria-label="Close"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 6L14 14M14 6L6 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <AlertDialogHeader>
          <AlertDialogTitle>Product Created Successfully</AlertDialogTitle>
          <AlertDialogDescription>
            Your product has been created. What would you like to do next?
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="sm:justify-center gap-1 flex-col sm:flex-row mt-3">
          <AlertDialogAction
            onClick={handleCopyLink}
            className="bg-white text-primary border border-gray-200 hover:bg-gray-100 flex-1 sm:flex-initial sm:min-w-[140px] text-center justify-center"
          >
            Copy payment link
          </AlertDialogAction>
          <div className="flex-1 sm:flex-initial sm:min-w-[160px] flex flex-col items-center">
            <AlertDialogAction
              onClick={handleCreateBundle}
              className="bg-primary text-white hover:bg-primary/90 w-full text-center justify-center"
            >
              Create Checkout Page
            </AlertDialogAction>
            <p className="text-xs text-gray-500 mt-1 text-center px-2">
              Use this to combine setup fees with recurring subscriptions.
            </p>
          </div>
          <AlertDialogAction
            onClick={handleCreateAnotherProduct}
            className="bg-green-600 text-white hover:bg-green-700 flex-1 sm:flex-initial sm:min-w-[180px] text-center justify-center"
          >
            Create Another Product
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
